<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { storeToRefs } from "pinia"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    useScope: "local",
    messages: {
        fr: {
            warn: {
                title: "Le workflow n'est pas terminé",
                content:
                    "La comparaison sera possible une fois toutes les étapes terminées.",
                delayed_cdc:
                    "La courbe de charge n'était pas activée sur ce compteur. L'activation a été demandée à Enedis — comptez jusqu'à 3 jours avant la première remontée.",
            },
            status: {
                0: "En attente",
                1: "En cours",
                2: "Terminé",
                3: "Erreur",
                4: "Signé",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface WorkflowStep {
    status: number
    name: string
}

// ================================
// REACTIVE STATE
// ================================
const comparatorStore = useComparatorStore()
const { getWorkflowEventsBySourceId } = comparatorStore
const { sources, sourceSelected } = storeToRefs(comparatorStore)

// ================================
// COMPUTED PROPERTIES
// ================================
/**
 * Vérifie si le workflow est en cours d'exécution
 * @returns {boolean} True si au moins une étape est en cours
 */
const isWorkflowInProgress = computed(() =>
    getWorkflowEventsBySourceId.some((step: WorkflowStep) => step.status === 1)
)

/**
 * Vrai si la source courante a reçu l'activation CDC mais aucune donnée de courbe
 * n'est encore remontée (jusqu'à 3 jours d'attente côté Enedis).
 */
const isDelayedCdc = computed(() => {
    const source = sources.value.find(
        (s) => s._id_source === sourceSelected.value
    )
    return (
        source?.informations?.pdl?.data_retrieval?.historical?.state ===
        "delayed"
    )
})

// ================================
// METHODS
// ================================
/**
 * Retourne l'icône PrimeVue appropriée selon le statut de l'étape
 * @param {number} status - Le statut numérique de l'étape
 * @returns {string} La classe CSS de l'icône PrimeVue
 */
const getIconByStatus = (status: number): string => {
    switch (status) {
        case 0: // En attente
            return "pi pi-pause"
        case 1: // En cours
            return "pi pi-spinner pi-spin"
        case 3: // Erreur
            return "pi pi-times text-gray-500"
        case 2: // Terminé
        case 4: // Signé
            return "pi pi-check text-green-500"
        default:
            return "pi pi-question"
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div
        class="comparator-meter-workflow-visualiser card p-4 flex flex-col gap-4"
    >
        <!-- ================================ -->
        <!-- MESSAGE D'ALERTE WORKFLOW -->
        <!-- ================================ -->
        <BaseMessage
            severity="warn"
            icon="pi pi-exclamation-triangle"
            v-if="isWorkflowInProgress"
        >
            <div class="flex flex-col gap-2">
                <p class="font-semibold">{{ t("warn.title") }}</p>
                <p class="text-sm">{{ t("warn.content") }}</p>
                <p v-if="isDelayedCdc" class="text-sm">
                    {{ t("warn.delayed_cdc") }}
                </p>
            </div>
        </BaseMessage>

        <!-- ================================ -->
        <!-- TIMELINE DES ÉTAPES WORKFLOW -->
        <!-- ================================ -->
        <BaseTimeline :value="getWorkflowEventsBySourceId" align="right">
            <!-- Statut de l'étape -->
            <template #opposite="slotProps">
                <div class="flex gap-2 items-center text-sm">
                    <p class="text-gray-600">
                        {{ t(`status.${slotProps.item?.status}`) }}
                    </p>
                    <i :class="getIconByStatus(slotProps.item?.status)"></i>
                </div>
            </template>
            <!-- Nom de l'étape -->
            <template #content="slotProps">
                <p class="text-sm font-bold">{{ slotProps.item.name }}</p>
            </template>
        </BaseTimeline>
    </div>
</template>
